import { useState } from "react";
import { buildClasses } from "../utils/buildClasses";
import { RyeButton } from "./RyeButton";
import { RyeVariant, RyeVibe } from "./RyeConsts";
import {
  useFloating,
  offset,
  flip,
  shift,
  size as floatingSize,
  useInteractions,
  useDismiss,
  FloatingPortal,
  Placement,
  autoUpdate,
} from "@floating-ui/react";
import { useRyeMenuTransition } from "./useRyeMenuTransition";

export type ButtonMenuEntry = {
  content: React.ReactNode;
  closeOnClick?: boolean;
};

export function RyeButtonMenu({
  text,
  icon,
  menuEntries = [],
  variant = "outlined",
  vibe = "ordinary",
  size = "md",
  shape = "rounded-rect",
  shadow = "none",
  disabled = false,
  iconColor,
  iconSize,
  iconFill = false,
  primaryColor,
  align = "left",
  tooltip,
}: {
  text?: string;
  icon?: string;
  menuEntries?: ButtonMenuEntry[];
  size?: "xs" | "sm" | "md" | "lg";
  shape?: "rounded-rect" | "fully-round";
  shadow?: "none" | "sm" | "md" | "lg";
  variant?: RyeVariant;
  vibe?: RyeVibe;
  disabled?: boolean;
  iconColor?: "red" | "yellow" | "green" | "purple";
  iconSize?: "xs" | "sm" | "md" | "lg" | "xl";
  iconFill?: boolean;
  primaryColor?: "yellow" | "green" | "red" | "purple";
  align?: "left" | "right" | "center";
  highlightOnHover?: boolean;
  tooltip?: string;
}): JSX.Element {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuMinWidth, setMenuMinWidth] = useState<number | undefined>(
    undefined,
  );

  const placementMap: Record<string, Placement> = {
    left: "bottom-start",
    right: "bottom-end",
    center: "bottom",
  };

  const { refs, floatingStyles, context } = useFloating({
    open: isMenuOpen,
    onOpenChange: setIsMenuOpen,
    placement: placementMap[align] || "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift(),
      floatingSize({
        apply({ rects }) {
          setMenuMinWidth(rects.reference.width);
        },
      }),
    ],
  });

  const dismiss = useDismiss(context);
  const { getFloatingProps } = useInteractions([dismiss]);
  const menuTransition = useRyeMenuTransition(context);

  function handleButtonClick() {
    if (disabled) return;
    setIsMenuOpen(!isMenuOpen);
  }

  function handleMenuItemClick(entry: ButtonMenuEntry, index: number) {
    if (entry.closeOnClick !== false) {
      setIsMenuOpen(false);
    }
  }

  return (
    <div ref={refs.setReference} className={buildClasses("inline-block")}>
      <RyeButton
        text={text}
        icon={icon}
        onClick={handleButtonClick}
        variant={variant}
        vibe={vibe}
        size={size}
        shape={shape}
        shadow={shadow}
        disabled={disabled}
        iconColor={iconColor}
        iconSize={iconSize}
        iconFill={iconFill}
        primaryColor={primaryColor}
        tooltip={tooltip}
      />

      {menuTransition.isMounted && menuEntries.length > 0 && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, minWidth: menuMinWidth }}
            {...getFloatingProps()}
            className={buildClasses("z-[2000000000]")}
          >
            {/* Animated apart from the outer element, whose transform places it. */}
            <div
              style={menuTransition.styles}
              className={buildClasses(
                menuTransition.className,
                "bg-surface-50",
                "border-tiny",
                "border-on-surface-200",
                "rounded-md",
                "shadow-popover",
                "overflow-hidden",
              )}
            >
              {menuEntries.map((entry, index) => (
                <div
                  key={`menu-item-${index}`}
                  className={buildClasses(
                    {
                      switch: size,
                      cases: new Map([
                        ["xs", buildClasses("h-6", "text-xs", "px-2")],
                        ["sm", buildClasses("h-7", "text-sm", "px-2.5")],
                        ["md", buildClasses("h-9", "text-md", "px-3")],
                        ["lg", buildClasses("h-11", "text-lg", "px-3.5")],
                      ]),
                    },
                    "bg-surface-50",
                    "flex",
                    "items-center",
                    "text-left",
                    "whitespace-nowrap",
                    "border-none",
                    "w-full",
                  )}
                  onClick={() => handleMenuItemClick(entry, index)}
                >
                  {entry.content}
                </div>
              ))}
            </div>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
}
